import { useState, useEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import { Form, Button as BsButton, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { Diagram3, InfoCircleFill, People, Person, Trash } from "react-bootstrap-icons";
import "./FacilityParkerInvitePage.css";
import { PageTitle } from "./Controls/PageTitle";
import { Select } from "./Controls/Select";
import { useForm } from "react-hook-form";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { MediumPage } from "./Controls/MediumPage";
import { Navigate, useLoaderData, useNavigate, useLocation, useRouteLoaderData } from "react-router-dom";
import { Api } from "./Data/Api";
import { useParamIds } from "./Data/Common";
import { FacilityInviteCredentialCapability, InviteParkerFeeReq, InviteParkerReq, Plan, Facility, SubscriptionFeeCadence } from "./Data/ApiTypes";
import { showMoney, toCents } from "./Data/Money";
import { showDate } from "./Data/Date";
import { LocalDate } from '@js-joda/core';
import { DateTimeFormatter } from '@js-joda/core';
import { FieldError } from 'react-hook-form';
import { maskAtom } from "./Data/Atoms";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import { FEATURES, isFeatureEnabled } from "./Data/Features";
import { useAuthorization } from "./hooks/useAuthorization";

function getDayWithSuffix(day: number): string {
    if (day >= 11 && day <= 13) return `${day}th`;
    switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
    }
}

type PaymentType = "immediate" | "deferred";
type GroupOption = "none" | "groupAccount" | "enhancedGroup";

/** Draft fee row — amount kept as typed text so decimals work while editing. */
type InviteFeeRowDraft = {
    description: string;
    amountText: string;
    cadence: SubscriptionFeeCadence;
};

type InviteFeeRowErrors = {
    description?: string;
    amount?: string;
};

function parseFeeAmountCents(amountText: string): number {
    const dollars = parseFloat(amountText);
    if (!Number.isFinite(dollars) || dollars <= 0)
        return 0;
    return Math.round(dollars * 100);
}

/** Every added fee row must have description + amount > 0 (or be removed). */
function validateInviteFeeRows(rows: InviteFeeRowDraft[]): InviteFeeRowErrors[] {
    return rows.map(row => {
        const errors: InviteFeeRowErrors = {};
        if (!row.description.trim())
            errors.description = "Description is required";
        if (parseFeeAmountCents(row.amountText) <= 0)
            errors.amount = "Enter an amount greater than $0.00";
        return errors;
    });
}

function inviteFeeRowsHaveErrors(errors: InviteFeeRowErrors[]): boolean {
    return errors.some(e => e.description || e.amount);
}

type InviteConfirmSummary = {
    name: string;
    planName: string;
    priceLabel: string;
    quantity: number;
    startDateLabel: string;
    outcomeMessage: string;
};

function getInviteOutcomeMessage(
    plan: Plan | undefined,
    priceRow: Plan["prices"][number] | undefined,
    paymentType: PaymentType,
): string {
    const complimentary = priceRow !== undefined && toCents(priceRow.price) === 0;
    const isUsageBased = plan?.isUsageBasedBilling ?? false;
    const requiresImmediatePayment = !complimentary && paymentType === "immediate";

    if (!requiresImmediatePayment && !isUsageBased) {
        if (complimentary) {
            return "The subscription will be activated immediately.";
        }
        return "The subscription will be activated immediately and the account will be invoiced.";
    }

    if (requiresImmediatePayment) {
        return "The account will be invited and must complete registration.  The subscription will be activated and payment will be captured upon approval.";
    }

    return "The account will receive an invitation email to complete setup.";
}

function formatInviteStartDate(startDate: InviteParkerReq["startDate"] | string | null | undefined): string {
    if (!startDate) {
        return "—";
    }
    if (startDate instanceof LocalDate) {
        return showDate(startDate);
    }
    if (typeof startDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
        const [year, month, day] = startDate.split("-").map(Number);
        return showDate(LocalDate.of(year, month, day));
    }
    return String(startDate);
}

function buildInviteConfirmSummary(
    data: InviteParkerReq,
    plans: Plan[],
    paymentType: PaymentType,
): InviteConfirmSummary {
    const plan = plans.find((p) => p.planId === data.planId);
    const priceRow = plan?.prices.find((pp) => pp.planPriceId === data.planPriceId);

    let priceLabel = "—";
    if (plan?.isUsageBasedBilling) {
        priceLabel = "Usage based";
    } else if (priceRow && toCents(priceRow.price) === 0) {
        priceLabel = "Complimentary";
    } else if (priceRow) {
        priceLabel = showMoney(priceRow.price);
    }

    const startDateLabel = plan?.isUsageBasedBilling
        ? showDate(LocalDate.now())
        : formatInviteStartDate(data.startDate);

    return {
        name: data.name.trim(),
        planName: plan?.name ?? "—",
        priceLabel,
        quantity: data.quantity ?? 1,
        startDateLabel,
        outcomeMessage: getInviteOutcomeMessage(plan, priceRow, paymentType),
    };
}

function InviteConfirmDialog({
    show,
    summary,
    loading,
    onHide,
    onConfirm,
}: {
    show: boolean;
    summary: InviteConfirmSummary | null;
    loading: boolean;
    onHide: () => void;
    onConfirm: () => void;
}) {
    if (!summary) {
        return null;
    }

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="invite-confirm-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <div>
                    <Modal.Title className="invite-confirm-title">Confirm Invitation</Modal.Title>
                    <p className="invite-confirm-subtitle mb-0">
                        Review these details before sending the invite.
                    </p>
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <div className="invite-confirm-panel">
                    {([
                        ["Name", summary.name],
                        ["Plan", summary.planName],
                        ["Price / Space", summary.priceLabel],
                        ["Quantity", String(summary.quantity)],
                        ["Start Date", summary.startDateLabel],
                    ] as const).map(([label, value]) => (
                        <div key={label} className="invite-confirm-row">
                            <span className="invite-confirm-label">{label}</span>
                            <span className="invite-confirm-value">{value}</span>
                        </div>
                    ))}
                </div>
                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 mt-3 small">
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>{summary.outcomeMessage}</span>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button type="button" onClick={onHide} disabled={loading}>
                    Go Back
                </Button>
                <Button type="button" className="btn-main" loading={loading} onClick={onConfirm}>
                    Send Invite
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

type PopperConfig = NonNullable<ComponentProps<typeof OverlayTrigger>["popperConfig"]>;

const overlayPopperConfig: PopperConfig = {
    strategy: "fixed",
    modifiers: [
        {
            name: "offset",
            options: {
                offset: [0, 10],
            },
        },
        {
            name: "preventOverflow",
            options: {
                padding: 8,
                boundary: "viewport",
            },
        },
        {
            name: "flip",
            options: {
                fallbackPlacements: ["bottom", "left", "right"],
            },
        },
    ],
};

function InfoPopover({ id, title, body }: { id: string; title: string; body: ReactNode }) {
    return (
        <OverlayTrigger
            placement="top"
            container={document.body}
            popperConfig={overlayPopperConfig}
            overlay={
                <Popover id={id} style={{ pointerEvents: "none", maxWidth: 280 }}>
                    <Popover.Header as="h4">{title}</Popover.Header>
                    <Popover.Body>{body}</Popover.Body>
                </Popover>
            }
        >
            <span
                className="d-inline-flex align-items-center group-option-info-trigger"
                style={{ cursor: "help", marginLeft: "4px", width: "16px", height: "16px" }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InfoCircleFill size={14} />
            </span>
        </OverlayTrigger>
    );
}

type GroupOptionCardConfig = {
    value: GroupOption;
    title: string;
    description: string;
    icon: ReactNode;
    popoverTitle: string;
    popoverBody: string;
};

function GroupOptionCard({
    option,
    selected,
    disabled,
    onSelect,
}: {
    option: GroupOptionCardConfig;
    selected: boolean;
    disabled: boolean;
    onSelect: (value: GroupOption) => void;
}) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.title}
            className={`group-option-card${selected ? " selected" : ""}`}
            disabled={disabled}
            onClick={() => onSelect(option.value)}
        >
            <div className="group-option-card-header">
                <span className="group-option-card-icon" aria-hidden="true">
                    {option.icon}
                </span>
                <span className="group-option-card-title">{option.title}</span>
                <span className="group-option-card-actions">
                    <InfoPopover
                        id={`${option.value}-popover`}
                        title={option.popoverTitle}
                        body={option.popoverBody}
                    />
                    {selected && <span className="group-option-card-check" aria-hidden="true">✓</span>}
                </span>
            </div>
            <p className="group-option-card-desc">{option.description}</p>
        </button>
    );
}

export function FacilityParkerInvitePage() {
    const nav = useNavigate();
    const location = useLocation();
    const plans = useLoaderData() as Plan[];
    const facility = useRouteLoaderData("facility") as Facility;
    const { facilityId } = useParamIds();
    const { canPerformAction } = useAuthorization();
    const canManageMonthly = canPerformAction('manage-monthly');
    const [msg, setMsg] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [showInviteConfirmDialog, setShowInviteConfirmDialog] = useState(false);
    const [pendingInviteData, setPendingInviteData] = useState<InviteParkerReq | null>(null);
    const [inviteConfirmSummary, setInviteConfirmSummary] = useState<InviteConfirmSummary | null>(null);
    const [paymentType, setPaymentType] = useState<PaymentType>("immediate");
    const [groupOption, setGroupOption] = useState<GroupOption>("none");
    const [credentialCapability, setCredentialCapability] = useState<FacilityInviteCredentialCapability>({
        hasCredentialUpdateIntegration: false,
        rfidValidationPattern: null,
        rfidValidationMaxLength: null
    });
    const [feeRows, setFeeRows] = useState<InviteFeeRowDraft[]>([]);
    const [feeRowErrors, setFeeRowErrors] = useState<InviteFeeRowErrors[]>([]);
    const feeDescRefs = useRef<(HTMLInputElement | null)[]>([]);
    const focusFeeDescIndex = useRef<number | null>(null);
    const [, setMask] = useAtom(maskAtom);

    useEffect(() => {
        const idx = focusFeeDescIndex.current;
        if (idx == null) return;
        focusFeeDescIndex.current = null;
        requestAnimationFrame(() => feeDescRefs.current[idx]?.focus());
    }, [feeRows.length]);
    
    const isEnhancedGroupsEnabled = isFeatureEnabled(facility, FEATURES.ENHANCED_GROUPS);

    const prefillData = location.state as { email?: string; name?: string } | null;

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<InviteParkerReq>({
        defaultValues: {
            name: prefillData?.name || "",
            emailAddress: prefillData?.email || "",
            phoneNumber: "",
            message: "",
            quantity: 1,
            planId: plans?.[0]?.planId ?? 0,
            planPriceId: plans?.[0]?.prices?.[0]?.planPriceId ?? 0,
            startDate: LocalDate.now(),
            IsEnhancedGroup: false,
            IsGroup: false,
            pendingIntegrationCredential: null
        }
    });

    const selectedPlanId = watch("planId");
    const selectedPlanPriceId = watch("planPriceId");
    const prices = plans.find(p => p.planId === selectedPlanId)?.prices || [];
    const selectedPlan = plans.find(p => p.planId === selectedPlanId);
    const invoiceDay = selectedPlan?.dayOfMonthInvoice ?? 1;
    const selectedPriceEntry = prices.find(p => p.planPriceId === selectedPlanPriceId);
    const isUsageBasedBilling = selectedPlan?.isUsageBasedBilling ?? false;
    const isComplimentary = selectedPriceEntry !== undefined && toCents(selectedPriceEntry.price) === 0;
    const isEnhancedGroup = groupOption === "enhancedGroup";
    const isGroupAccount = groupOption === "groupAccount";
    const isDeferredPayment = paymentType === "deferred";
    const showAdditionalFees =
        !isComplimentary && isFeatureEnabled(facility, FEATURES.SUBSCRIPTION_FEES);
    const showCredentialField = credentialCapability.hasCredentialUpdateIntegration
        && paymentType === "immediate"
        && !isEnhancedGroup
        && !isComplimentary
        && !isUsageBasedBilling;
    const maxCredentialLength = credentialCapability.rfidValidationMaxLength ?? 20;

    useEffect(() => {
        let ignore = false;
        Api.facilityParkerInviteCredentialCapability(facilityId).then(res => {
            if (!ignore && res.isOk) {
                setCredentialCapability(res.value);
            }
        });

        return () => {
            ignore = true;
        };
    }, [facilityId]);

    // Reset price when plan changes
    useEffect(() => {
        if (prices.length > 0) {
            setValue("planPriceId", prices[0].planPriceId);
        }
    }, [selectedPlanId, prices, setValue]);

    const handleGroupOptionChange = (option: GroupOption) => {
        setGroupOption(option);
        setValue("IsEnhancedGroup", option === "enhancedGroup");
        setValue("IsGroup", option === "groupAccount");
        if (option === "enhancedGroup") {
            setPaymentType("deferred");
        }
    };

    const handlePaymentTypeChange = (nextPaymentType: PaymentType) => {
        setPaymentType(nextPaymentType);
        if (nextPaymentType === "immediate") {
            setGroupOption("none");
            setValue("IsEnhancedGroup", false);
            setValue("IsGroup", false);
        }
    };

    // Complimentary plans do not use group options or payment timing UI
    useEffect(() => {
        if (isComplimentary) {
            setGroupOption("none");
            setValue("IsEnhancedGroup", false);
            setValue("IsGroup", false);
            setFeeRows([]);
            setFeeRowErrors([]);
        }
    }, [isComplimentary, setValue]);

    useEffect(() => {
        if (isUsageBasedBilling) {
            setValue("startDate", LocalDate.now());
        }
    }, [isUsageBasedBilling, setValue]);

    useEffect(() => {
        if (!showCredentialField) {
            setValue("pendingIntegrationCredential", null);
        }
    }, [showCredentialField, setValue]);

    const vals = watch();

    if (plans.length === 0) {
        return <Navigate to={`/facility/${facilityId}`} />;
    }

    const finalizeSubmit = async (data : InviteParkerReq) => {
        setLoading(true);
        const plan = plans.find(p => p.planId === data.planId);
        const priceRow = plan?.prices.find(pp => pp.planPriceId === data.planPriceId);
        const complimentary = priceRow ? toCents(priceRow.price) === 0 : false;
        const fees: InviteParkerFeeReq[] = showAdditionalFees
            ? feeRows.map(f => ({
                description: f.description.trim(),
                amountCents: parseFeeAmountCents(f.amountText),
                // Release: one-time only; restore cadence from row when monthly is re-enabled.
                cadence: 0 as SubscriptionFeeCadence,
            }))
            : [];
        const res = await Api.facilityParkerInvite(facilityId, {
            ...data,
            requirePayment: complimentary ? false : (isEnhancedGroup ? false : paymentType === "immediate"),
            IsEnhancedGroup: complimentary ? false : isEnhancedGroup,
            IsGroup: complimentary ? false : isGroupAccount,
            pendingIntegrationCredential: showCredentialField ? data.pendingIntegrationCredential : null,
            fees: fees.length > 0 ? fees : null,
        });
        if (res.isOk) {
            setMsg(typeof res.value === 'string' ? res.value : '');
           
            
            // Extract subscription ID and parker ID from response object
            let subscriptionId: number | null = null;
            let parkerId: number | null = null;
            
            if (typeof res.value === 'object' && res.value !== null) {
                subscriptionId = (res.value as any).subscriptionId ?? null;
                parkerId = (res.value as any).parkerId ?? null;
            }
            
            // If we have subscription info, redirect to subscription details page
            if (isEnhancedGroup && subscriptionId && parkerId) {
                toast.success("Subscription saved and is ready for setup.");
                nav(`/facility/${facilityId}/parker/${parkerId}/plan/${subscriptionId}`, {
                    state: { fromInvite: true, from: "invite" }
                });
            } else {
                toast.success("Invitation sent successfully");
                nav(`/facility/${facilityId}/parker`);
            }
            return;            
        }

        setLoading(false);
        toast.error(`There was a problem with the invitation: ${res.error}.`);
        setMsg(res.error);
    };

    const handleFormSubmit = (data: InviteParkerReq) => {
        if (showAdditionalFees) {
            const nextFeeErrors = validateInviteFeeRows(feeRows);
            if (inviteFeeRowsHaveErrors(nextFeeErrors)) {
                setFeeRowErrors(nextFeeErrors);
                toast.error("Complete or remove additional fees before sending the invite.");
                return;
            }
        }
        setFeeRowErrors([]);

        if (isEnhancedGroup) {
            void finalizeSubmit(data);
            return;
        }

        setPendingInviteData(data);
        setInviteConfirmSummary(buildInviteConfirmSummary(data, plans, paymentType));
        setShowInviteConfirmDialog(true);
    };

    const handleInviteConfirm = () => {
        if (!pendingInviteData) {
            return;
        }

        void finalizeSubmit(pendingInviteData);
        setShowInviteConfirmDialog(false);
        setPendingInviteData(null);
        setInviteConfirmSummary(null);
    };

    const handleInviteConfirmHide = () => {
        if (loading) {
            return;
        }

        setShowInviteConfirmDialog(false);
        setPendingInviteData(null);
        setInviteConfirmSummary(null);
    };

    if (!canManageMonthly) {
        return <Navigate to={`/facility/${facilityId}/parker/active`} replace />;
    }

    return (
        <MediumPage>
             <div className="d-flex align-items-center gap-3">
            <BsButton variant="link" className="p-0" onClick={() => nav(`/facility/${facilityId}/parker`)}>  ← Back</BsButton>
            <PageTitle>
                Invite
            
            </PageTitle>
        </div>
         
            <Form onSubmit={handleSubmit(handleFormSubmit)}>
                <Input
                    type="text"
                    label="Name"
                    {...register("name", { required: "Name is required" })}
                    error={errors.name}
                />

                <Input
                    type="email"
                    label="Email"
                    {...register("emailAddress", {
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    })}
                    error={errors.emailAddress}
                />

                <Input
                    type="tel"
                    label="Phone Number (Optional)"
                    {...register("phoneNumber")}
                    error={errors.phoneNumber}
                />

                <Select label="Plan"
                    {...register("planId", { valueAsNumber: true, required: "Plan is required" })}
                    error={errors.planId}>
                    {plans.map( plan => (
                        <option key={plan.planId} value={plan.planId}>
                            {plan.name}
                        </option>
                    ))}
                </Select>

                {!isUsageBasedBilling && (
                    <Select
                        label="Price"
                        {...register("planPriceId", {
                            required: "Price is required",
                            valueAsNumber: true } ) }
                        error={errors.planPriceId}>
                        {prices.map( price => (
                            <option key={price.planPriceId}
                                    value={price.planPriceId}>
                                {toCents(price.price) === 0 ? "Complimentary" : showMoney(price.price)}
                            </option>
                        ))}
                    </Select>
                )}
                
                {!isComplimentary && (
                    <Form.Group className="mb-3">
                        <Form.Label className="mb-1">
                            Payment Type{' '}
                            <InfoPopover
                                id="payment-type-popover"
                                title="Payment Type"
                                body={
                                    <>
                                        <div>Choose how the parker will be required to pay.</div>
                                        <div className="mt-2">
                                            <b>Require Payment Immediately</b> — The parker will be prompted to enter payment details at checkout.
                                        </div>
                                        <div className="mt-2">
                                            <b>Allow Deferred Invoicing/Payment</b> — The parker will skip payment for now and receive an invoice on the {getDayWithSuffix(invoiceDay)} of each month.
                                            <p className="mt-3" style={{ fontSize: ".8rem" }}><b>Note:</b> If invoice day has passed, the parker will receive a prorated invoice for the remainder of this month and another for the next.</p>
                                        </div>
                                    </>
                                }
                            />
                        </Form.Label>
                        <Form.Select
                            value={paymentType}
                            onChange={(e) => handlePaymentTypeChange(e.target.value as PaymentType)}>
                            <option value="immediate">Require Payment Immediately</option>
                            <option value="deferred">Allow Deferred Invoicing/Payment</option>
                        </Form.Select>
                    </Form.Group>
                )}

                {!isComplimentary && (
                    <Form.Group className="mb-3">
                        <Form.Label className="mb-2">Account Type</Form.Label>
                        <div className="group-option-section">
                            {!isDeferredPayment && (
                                <div className="group-option-callout">
                                    <span>
                                        Group subscriptions need to start as deferred invoicing.{" "}
                                        <button
                                            type="button"
                                            className="group-option-callout-action"
                                            onClick={() => handlePaymentTypeChange("deferred")}
                                        >
                                            Switch to deferred invoicing
                                        </button>
                                        {" "}
                                        <InfoPopover
                                            id="group-deferred-callout-tip"
                                            title="Auto-pay"
                                            body="The account can enroll in auto-pay later if they choose."
                                        />
                                    </span>
                                </div>
                            )}
                            <div className="group-option-cards" role="radiogroup" aria-label="Group">
                                <GroupOptionCard
                                    option={{
                                        value: "none",
                                        title: "Standard",
                                        description: "Standard subscription for individuals or small number of parkers.",
                                        icon: <Person size={15} />,
                                        popoverTitle: "Standard",
                                        popoverBody: "A standard account that does not require a group subscription.",
                                    }}
                                    selected={groupOption === "none"}
                                    disabled={false}
                                    onSelect={handleGroupOptionChange}
                                />
                                <GroupOptionCard
                                    option={{
                                        value: "groupAccount",
                                        title: "Group account",
                                        description: "Company is paying on behalf of multiple parkers with a flat rate per space and no pricing tiers.",
                                        icon: <People size={15} />,
                                        popoverTitle: "Group account",
                                        popoverBody: "A group account that contains multiple parkers on a single subscription, without pricing tiers.",
                                    }}
                                    selected={groupOption === "groupAccount"}
                                    disabled={!isDeferredPayment}
                                    onSelect={handleGroupOptionChange}
                                />
                                {isEnhancedGroupsEnabled && (
                                    <GroupOptionCard
                                        option={{
                                            value: "enhancedGroup",
                                            title: "Enhanced Group",
                                            description: "A group account that requires tiered pricing / contracts based on assigned groups",
                                            icon: <Diagram3 size={15} />,
                                            popoverTitle: "Enhanced Group",
                                            popoverBody: "Sub-groups and contracts can be created within the subscription before the invitation email is sent.",
                                        }}
                                        selected={groupOption === "enhancedGroup"}
                                        disabled={!isDeferredPayment}
                                        onSelect={handleGroupOptionChange}
                                    />
                                )}
                            </div>
                            {isEnhancedGroup && (
                                <p className="group-option-footnote">
                                    The account will receive an invoice after the subscription is configured.
                                </p>
                            )}
                        </div>
                    </Form.Group>
                )}

                {!isUsageBasedBilling && (
                    <Input
                        type="date"
                        label="Start Date"
                        {...register("startDate", { 
                            required: "Start date is required",
                            validate: {
                                validDate: (value: any) => {
                                    // Skip validation if empty - required will handle this
                                    if (!value || value === '') return true;
                                    
                                    // If it's already a LocalDate, it's valid
                                    if (value instanceof LocalDate) return true;
                                    
                                    // Must be a string in YYYY-MM-DD format
                                    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                        return "Date must be in YYYY-MM-DD format";
                                    }

                                    try {
                                        const [year, month, day] = value.split('-').map(Number);
                                        const date = LocalDate.of(year, month, day);
                                        const firstOfMonth = LocalDate.now().withDayOfMonth(1);
                                        return date.isAfter(firstOfMonth.minusDays(1)) || "Start date must be on or after the 1st of this month";
                                    } catch (e) {
                                        return "Invalid date (e.g., February 30th is not valid)";
                                    }
                                }
                            },
                            setValueAs: (value: string | null) => {
                                if (!value) return value;
                                try {
                                    const [year, month, day] = value.split('-').map(Number);
                                    return LocalDate.of(year, month, day);
                                } catch (e) {
                                    return value; // Return original value if parsing fails
                                }
                            }
                        })}
                        min={LocalDate.now().withDayOfMonth(1).format(DateTimeFormatter.ISO_LOCAL_DATE).toString()}
                        error={errors.startDate as FieldError}
                    />
                )}
                
                {!isUsageBasedBilling && (
                    <Input
                        type="number"
                        label="Quantity"
                        min="1"
                        {...register("quantity", {
                            required: "Quantity is required",
                            valueAsNumber: true,
                            min: { value: 1, message: "Quantity must be at least 1" },
                        })}
                        error={errors.quantity} />
                )}
                {showAdditionalFees && (
                <Form.Group className="mb-3 invite-fees-section">
                    <Form.Label className="mb-1 invite-fees-title">
                        Additional fees (optional){' '}
                        <InfoPopover
                            id="subscription-goods-fee-popover"
                            title="Additional fees"
                            body="One-time charges for goods/services (e.g. key fobs). Separate from platform service fees."
                        />
                    </Form.Label>
                    <p className="invite-fees-help">
                        Add one-time charges to this subscription, like key fobs or other one-time upcharges.
                    </p>
                    <div className="invite-fees-list">
                        {feeRows.map((row, idx) => (
                            <div key={idx} className="invite-fee-card">
                                <div className="invite-fee-card-header">
                                    <span className="invite-fee-card-label">Fee {idx + 1}</span>
                                    <button
                                        type="button"
                                        className="invite-fee-remove"
                                        aria-label={`Remove fee ${idx + 1}`}
                                        onClick={() => {
                                            setFeeRows(feeRows.filter((_, i) => i !== idx));
                                            setFeeRowErrors(feeRowErrors.filter((_, i) => i !== idx));
                                        }}
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                                <div className="invite-fee-card-fields">
                                    <Form.Group className="invite-fee-field invite-fee-field--desc">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            placeholder="e.g. Registration fee"
                                            value={row.description}
                                            isInvalid={!!feeRowErrors[idx]?.description}
                                            ref={(el: HTMLInputElement | null) => { feeDescRefs.current[idx] = el; }}
                                            onChange={e => {
                                                const next = [...feeRows];
                                                next[idx] = { ...next[idx], description: e.target.value };
                                                setFeeRows(next);
                                                if (feeRowErrors[idx]?.description) {
                                                    const nextErr = [...feeRowErrors];
                                                    nextErr[idx] = { ...nextErr[idx], description: undefined };
                                                    setFeeRowErrors(nextErr);
                                                }
                                            }}
                                        />
                                        {feeRowErrors[idx]?.description && (
                                            <Form.Text className="d-block text-danger">
                                                {feeRowErrors[idx].description}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                    <Form.Group className="invite-fee-field invite-fee-field--amount">
                                        <Form.Label>Amount</Form.Label>
                                        <div className="invite-fee-amount-input">
                                            <span className="invite-fee-amount-prefix" aria-hidden>$</span>
                                            <Form.Control
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0.00"
                                                value={row.amountText}
                                                isInvalid={!!feeRowErrors[idx]?.amount}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    // Allow empty / digits / one decimal / up to 2 fractional digits.
                                                    if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw))
                                                        return;
                                                    const next = [...feeRows];
                                                    next[idx] = { ...next[idx], amountText: raw };
                                                    setFeeRows(next);
                                                    if (feeRowErrors[idx]?.amount) {
                                                        const nextErr = [...feeRowErrors];
                                                        nextErr[idx] = { ...nextErr[idx], amount: undefined };
                                                        setFeeRowErrors(nextErr);
                                                    }
                                                }}
                                            />
                                        </div>
                                        {feeRowErrors[idx]?.amount && (
                                            <Form.Text className="d-block text-danger">
                                                {feeRowErrors[idx].amount}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                    {/* Billing cadence: one-time only for this release; restore dropdown for multi-select later.
                                    <Form.Group className="invite-fee-field invite-fee-field--billing">
                                        <Form.Label>Billing</Form.Label>
                                        <Form.Select
                                            value={row.cadence === 1 || row.cadence === "Recurring" ? "1" : "0"}
                                            onChange={e => {
                                                const next = [...feeRows];
                                                next[idx] = { ...next[idx], cadence: e.target.value === "1" ? 1 : 0 };
                                                setFeeRows(next);
                                            }}
                                        >
                                            <option value="0">One-time</option>
                                            <option value="1">Monthly</option>
                                        </Form.Select>
                                    </Form.Group>
                                    */}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="invite-fee-add"
                        onClick={() => {
                            focusFeeDescIndex.current = feeRows.length;
                            setFeeRows([...feeRows, {
                                description: "",
                                amountText: "",
                                cadence: 0 as SubscriptionFeeCadence,
                            }]);
                        }}
                    >
                        + Add fee
                    </button>
                </Form.Group>
                )}
                {showCredentialField && (
                    <Form.Group className="mb-3">
                        <Form.Label className="mb-1">
                            RFID Number (Optional){' '}
                            <InfoPopover
                                id="rfid-number-popover"
                                title="RFID Number"
                                body="Assign a physical RFID number to this parker when their application is approved."
                            />
                        </Form.Label>
                        <Form.Control
                            type="text"
                            maxLength={maxCredentialLength}
                            {...register("pendingIntegrationCredential", {
                                maxLength: {
                                    value: maxCredentialLength,
                                    message: `RFID number must be ${maxCredentialLength} characters or fewer`
                                },
                                setValueAs: (value: string | null) => {
                                    const trimmed = value?.trim();
                                    return trimmed ? trimmed : null;
                                }
                            })}
                        />
                        {errors.pendingIntegrationCredential &&
                            <Form.Text className="d-block text-danger ms-1">
                                {errors.pendingIntegrationCredential.message}
                            </Form.Text>}
                    </Form.Group>
                )}
                <div className="d-flex justify-content-end gap-2 mb-4">
                    <Button type="button" className="w-50" loading={loading}  onClick={() => nav(-1)}>
                        Cancel                     
                    </Button>
                    <Button type="submit" className="w-50 btn-main" loading={loading}>
                        {isEnhancedGroup ? "Continue Setup" : "Send Invite"}
                    </Button>
                </div>
            </Form>
            <InviteConfirmDialog
                show={showInviteConfirmDialog}
                summary={inviteConfirmSummary}
                loading={loading}
                onHide={handleInviteConfirmHide}
                onConfirm={handleInviteConfirm}
            />
        </MediumPage>
    );
}
